import { ApiError, type Feature, type Product } from '@central/api';
import { Card, PageHeader, StatusChip } from '@central/ui';
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Modal,
  Select,
  Switch,
  Table,
  TextInput,
  Textarea,
  VisuallyHidden,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { DataTable, Empty, QueryState } from '../components/parts';
import { errorMessage } from '../lib/errors';
import { mono, muted } from '../lib/labels';
import { useCanWrite } from '../lib/me';
import { Form } from './CustomerDialogs';

type Dialog =
  { kind: 'feature'; feature?: Feature } | { kind: 'product'; product?: Product } | undefined;

const reasonHelp = 'Say why; it goes to the audit log.';

/**
 * Products & features: what a license can grant. A product's feature order is
 * the order the license editor lists them in. Owners and Admins edit; every
 * write takes a reason for the audit log.
 */
export function Catalogue() {
  const api = useApi();
  const canWrite = useCanWrite();
  const [dialog, setDialog] = useState<Dialog>();
  const features = useQuery({ queryKey: keys.features, queryFn: () => api.catalogue.features() });
  const products = useQuery({ queryKey: keys.products, queryFn: () => api.catalogue.products() });
  const close = () => setDialog(undefined);
  const byId = new Map((features.data?.items ?? []).map((f) => [f.id, f]));

  return (
    <>
      <PageHeader
        title="Products & features"
        subtitle="What licenses grant. Licenses list a product’s features in the order set here."
      />
      <Card
        title="Products"
        padded={false}
        action={
          canWrite && (
            <Button size="xs" variant="default" onClick={() => setDialog({ kind: 'product' })}>
              New product
            </Button>
          )
        }
      >
        <QueryState
          isPending={products.isPending || features.isPending}
          error={products.error ?? features.error}
        >
          {() =>
            products.data!.items.length === 0 ? (
              <Empty>No products yet.</Empty>
            ) : (
              <DataTable label="Products" head={['Product', 'Features, in order', '']}>
                {products.data!.items.map((p) => (
                  <Table.Tr key={p.id}>
                    <Table.Td style={{ fontWeight: 600 }}>{p.name}</Table.Td>
                    <Table.Td style={{ whiteSpace: 'normal' }}>
                      {p.featureIds.length === 0 ? (
                        <span style={muted}>None</span>
                      ) : (
                        p.featureIds.map((id) => byId.get(id)?.name ?? id).join(' · ')
                      )}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {canWrite && (
                        <Button
                          variant="subtle"
                          size="xs"
                          onClick={() => setDialog({ kind: 'product', product: p })}
                        >
                          Edit <VisuallyHidden>{p.name}</VisuallyHidden>
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </DataTable>
            )
          }
        </QueryState>
      </Card>
      <Card
        title="Features"
        padded={false}
        action={
          canWrite && (
            <Button size="xs" variant="default" onClick={() => setDialog({ kind: 'feature' })}>
              New feature
            </Button>
          )
        }
      >
        <QueryState isPending={features.isPending} error={features.error}>
          {() =>
            features.data!.items.length === 0 ? (
              <Empty>No features yet.</Empty>
            ) : (
              <DataTable label="Features" head={['Feature', 'Key', 'Quantity', 'Unit', '']}>
                {features.data!.items.map((f) => (
                  <Table.Tr key={f.id}>
                    <Table.Td style={{ fontWeight: 600 }}>
                      {f.name}
                      {f.archived && (
                        <>
                          {' '}
                          <StatusChip tone="unk">Archived</StatusChip>
                        </>
                      )}
                    </Table.Td>
                    <Table.Td style={mono}>{f.key}</Table.Td>
                    <Table.Td>{f.isQuantity ? 'Yes' : 'No'}</Table.Td>
                    <Table.Td style={f.unit ? undefined : muted}>{f.unit ?? '–'}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {canWrite && (
                        <Button
                          variant="subtle"
                          size="xs"
                          onClick={() => setDialog({ kind: 'feature', feature: f })}
                        >
                          Edit <VisuallyHidden>{f.name}</VisuallyHidden>
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </DataTable>
            )
          }
        </QueryState>
      </Card>
      {/* Mounted only while open, so each opening starts from the stored row. */}
      {dialog?.kind === 'feature' && <FeatureModal feature={dialog.feature} onClose={close} />}
      {dialog?.kind === 'product' && (
        <ProductModal
          product={dialog.product}
          features={features.data?.items ?? []}
          onClose={close}
        />
      )}
    </>
  );
}

const fieldsOf = (e: unknown) => (e instanceof ApiError ? (e.fields ?? {}) : {});

function FeatureModal({ feature, onClose }: { feature?: Feature; onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [key, setKey] = useState(feature?.key ?? '');
  const [name, setName] = useState(feature?.name ?? '');
  const [isQuantity, setIsQuantity] = useState(feature?.isQuantity ?? false);
  const [unit, setUnit] = useState(feature?.unit ?? '');
  const [archived, setArchived] = useState(feature?.archived ?? false);
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const done = async () => {
    await queryClient.invalidateQueries({ queryKey: keys.features });
    onClose();
  };
  const save = useMutation({
    mutationFn: () => {
      const u = isQuantity ? unit.trim() : '';
      if (!feature)
        return api.catalogue.createFeature({
          key: key.trim(),
          name: name.trim(),
          isQuantity,
          unit: u || undefined,
          reason: reason.trim(),
        });
      return api.catalogue.updateFeature(feature.id, {
        name: name.trim(),
        isQuantity,
        unit: u,
        archived,
        reason: reason.trim(),
      });
    },
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: () => api.catalogue.deleteFeature(feature!.id, { reason: reason.trim() }),
    onSuccess: done,
    onSettled: () => setConfirmDelete(false),
  });
  const server = fieldsOf(save.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v: Record<string, string> = {};
    if (!feature && !key.trim()) v.key = 'Enter the key license tokens carry.';
    if (!name.trim()) v.name = 'Enter a name.';
    if (!reason.trim()) v.reason = reasonHelp;
    setErrors(v);
    if (Object.keys(v).length > 0) return;
    remove.reset();
    save.mutate();
  };
  const askDelete = () => {
    setErrors(reason.trim() ? {} : { reason: reasonHelp });
    if (reason.trim()) setConfirmDelete(true);
  };
  const confirmRemove = () => {
    save.reset();
    remove.mutate();
  };
  const fieldError = (f: string) =>
    errors[f] ?? (server[f] && `${f[0]!.toUpperCase()}${f.slice(1)}: ${server[f]}.`);
  return (
    <Modal opened onClose={onClose} title={feature ? `Edit ${feature.name}` : 'New feature'}>
      <Form
        onSubmit={submit}
        error={Object.keys(server).length ? undefined : save.error}
        submitLabel={feature ? 'Save feature' : 'Create feature'}
        pending={save.isPending}
        onCancel={onClose}
      >
        {feature ? (
          <div style={{ fontSize: 14 }}>
            Key <span style={mono}>{feature.key}</span>
            <div style={{ ...muted, fontSize: 13 }}>
              License tokens carry the key, so it cannot change.
            </div>
          </div>
        ) : (
          <TextInput
            label="Key"
            description="What license tokens carry and GoATMS matches, e.g. signals. It cannot change later."
            data-autofocus
            value={key}
            error={fieldError('key')}
            onChange={(e) => setKey(e.currentTarget.value)}
            styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
          />
        )}
        <TextInput
          label="Name"
          value={name}
          error={fieldError('name')}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <Switch
          label="Counts seats (a quantity per license)"
          description={feature && 'Fixed while a license lists the feature.'}
          checked={isQuantity}
          onChange={(e) => setIsQuantity(e.currentTarget.checked)}
        />
        {isQuantity && (
          <TextInput
            label="Unit"
            description="What a seat counts, e.g. signals"
            value={unit}
            error={fieldError('unit')}
            onChange={(e) => setUnit(e.currentTarget.value)}
          />
        )}
        {feature && (
          <Switch
            label="Archived: kept where it is, not added to products"
            checked={archived}
            onChange={(e) => setArchived(e.currentTarget.checked)}
          />
        )}
        <Textarea
          label="Reason (goes to the audit log)"
          value={reason}
          error={fieldError('reason')}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        {feature &&
          (confirmDelete ? (
            <Group justify="flex-start" gap={8}>
              <span style={{ fontSize: 14 }}>
                Delete {feature.name} for the reason above? This cannot be undone.
              </span>
              <Button color="red" size="xs" loading={remove.isPending} onClick={confirmRemove}>
                Delete {feature.name}
              </Button>
              <Button variant="default" size="xs" onClick={() => setConfirmDelete(false)}>
                Keep it
              </Button>
            </Group>
          ) : (
            <Group justify="flex-start">
              <Button variant="subtle" color="red" size="xs" onClick={askDelete}>
                Delete feature
              </Button>
              <span style={{ ...muted, fontSize: 13 }}>
                Only a feature no product or license lists. Takes the reason above.
              </span>
            </Group>
          ))}
        {!!remove.error && <Alert color="red">{errorMessage(remove.error)}</Alert>}
      </Form>
    </Modal>
  );
}

function ProductModal({
  product,
  features,
  onClose,
}: {
  product?: Product;
  features: Feature[];
  onClose: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [name, setName] = useState(product?.name ?? '');
  const [ids, setIds] = useState<string[]>(product?.featureIds ?? []);
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<{ name?: string; reason?: string }>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const byId = new Map(features.map((f) => [f.id, f]));
  const addable = features.filter((f) => !f.archived && !ids.includes(f.id));
  const done = async () => {
    await queryClient.invalidateQueries({ queryKey: keys.products });
    onClose();
  };
  const save = useMutation({
    mutationFn: () =>
      product
        ? api.catalogue.updateProduct(product.id, {
            name: name.trim(),
            featureIds: ids,
            reason: reason.trim(),
          })
        : api.catalogue.createProduct({
            name: name.trim(),
            featureIds: ids,
            reason: reason.trim(),
          }),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: () => api.catalogue.deleteProduct(product!.id, { reason: reason.trim() }),
    onSuccess: done,
    onSettled: () => setConfirmDelete(false),
  });
  const move = (i: number, by: -1 | 1) =>
    setIds((cur) => {
      const next = [...cur];
      [next[i], next[i + by]] = [next[i + by]!, next[i]!];
      return next;
    });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v: typeof errors = {};
    if (!name.trim()) v.name = 'Enter a name.';
    if (!reason.trim()) v.reason = reasonHelp;
    setErrors(v);
    if (Object.keys(v).length > 0) return;
    remove.reset();
    save.mutate();
  };
  const askDelete = () => {
    setErrors(reason.trim() ? {} : { reason: reasonHelp });
    if (reason.trim()) setConfirmDelete(true);
  };
  const confirmRemove = () => {
    save.reset();
    remove.mutate();
  };
  return (
    <Modal opened onClose={onClose} title={product ? `Edit ${product.name}` : 'New product'}>
      <Form
        onSubmit={submit}
        error={save.error}
        submitLabel={product ? 'Save product' : 'Create product'}
        pending={save.isPending}
        onCancel={onClose}
      >
        <TextInput
          label="Name"
          data-autofocus
          value={name}
          error={errors.name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
            Features, in the order licenses list them
          </div>
          {ids.length === 0 ? (
            <div style={{ ...muted, fontSize: 13 }}>No features yet.</div>
          ) : (
            <ol aria-label="Product features" style={{ margin: 0, paddingLeft: 20 }}>
              {ids.map((id, i) => {
                const f = byId.get(id);
                const label = f?.name ?? id;
                return (
                  <li key={id} style={{ padding: '4px 0' }}>
                    <Group gap={6} wrap="nowrap" justify="space-between">
                      <span style={{ minWidth: 0 }}>
                        {label}
                        {f && <span style={{ ...mono, ...muted }}> {f.key}</span>}
                      </span>
                      <Group gap={2} wrap="nowrap">
                        <ActionIcon
                          variant="subtle"
                          aria-label={`Move ${label} up`}
                          disabled={i === 0}
                          onClick={() => move(i, -1)}
                        >
                          ↑
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          aria-label={`Move ${label} down`}
                          disabled={i === ids.length - 1}
                          onClick={() => move(i, 1)}
                        >
                          ↓
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={`Remove ${label}`}
                          onClick={() => setIds((cur) => cur.filter((x) => x !== id))}
                        >
                          ×
                        </ActionIcon>
                      </Group>
                    </Group>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
        <Select
          label="Add a feature"
          placeholder={addable.length ? 'Choose a feature' : 'Every feature is listed'}
          disabled={addable.length === 0}
          searchable
          data={addable.map((f) => ({ value: f.id, label: `${f.name} (${f.key})` }))}
          value={null}
          onChange={(v) => v && setIds((cur) => [...cur, v])}
        />
        <Textarea
          label="Reason (goes to the audit log)"
          value={reason}
          error={errors.reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        {product &&
          (confirmDelete ? (
            <Group justify="flex-start" gap={8}>
              <span style={{ fontSize: 14 }}>
                Delete {product.name} for the reason above? Its features stay. This cannot be
                undone.
              </span>
              <Button color="red" size="xs" loading={remove.isPending} onClick={confirmRemove}>
                Delete {product.name}
              </Button>
              <Button variant="default" size="xs" onClick={() => setConfirmDelete(false)}>
                Keep it
              </Button>
            </Group>
          ) : (
            <Group justify="flex-start">
              <Button variant="subtle" color="red" size="xs" onClick={askDelete}>
                Delete product
              </Button>
              <span style={{ ...muted, fontSize: 13 }}>
                Only a product no license lists. Takes the reason above.
              </span>
            </Group>
          ))}
        {!!remove.error && <Alert color="red">{errorMessage(remove.error)}</Alert>}
      </Form>
    </Modal>
  );
}
